import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { BookOpen, Search, ChevronRight, ArrowLeft, Wrench, HelpCircle, Settings, Printer } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const CATS = {
  troubleshooting: { label: 'Troubleshooting', icon: Wrench, color: 'bg-red-100 text-red-700' },
  how_to: { label: 'How-To', icon: BookOpen, color: 'bg-blue-100 text-blue-700' },
  setup: { label: 'Setup Guide', icon: Settings, color: 'bg-emerald-100 text-emerald-700' },
  faq: { label: 'FAQ', icon: HelpCircle, color: 'bg-amber-100 text-amber-700' },
};

export default function KnowledgeBasePortal() {
  const [articles, setArticles] = useState([]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${BACKEND}/api/knowledge-base`)
      .then(r => setArticles(r.data.articles || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const platforms = useMemo(() => {
    const set = new Set();
    articles.forEach(a => { if (a.platform_name) set.add(a.platform_name); });
    return Array.from(set).sort();
  }, [articles]);

  const filtered = useMemo(() => {
    let result = articles;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(a => a.title.toLowerCase().includes(q) || a.content?.toLowerCase().includes(q) || a.platform_name?.toLowerCase().includes(q));
    }
    if (filterCat) result = result.filter(a => a.category === filterCat);
    if (filterPlatform) result = result.filter(a => a.platform_name === filterPlatform);
    return result;
  }, [articles, search, filterCat, filterPlatform]);

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  // Article detail view
  if (selected) {
    const cat = CATS[selected.category];
    return (
      <div className="min-h-screen bg-white print:bg-white" data-testid="kb-article-view">
        <header className="border-b px-4 py-3 print:border-b-2 sticky top-0 bg-white/95 backdrop-blur z-10">
          <div className="max-w-2xl mx-auto flex items-center justify-between">
            <button onClick={() => setSelected(null)} className="flex items-center gap-1.5 text-sm text-blue-600 hover:underline print:hidden">
              <ArrowLeft className="h-3.5 w-3.5" /> Back to articles
            </button>
            <Button variant="ghost" size="sm" onClick={() => window.print()} className="gap-1 text-xs print:hidden"><Printer className="h-3 w-3" /> Print</Button>
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-4 py-8">
          <div className="flex items-center gap-2 mb-3">
            <Badge variant="outline" className="text-xs">{selected.platform_name}</Badge>
            {cat && <Badge className={`text-[10px] ${cat.color}`}>{cat.label}</Badge>}
          </div>
          <h1 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{selected.title}</h1>
          <div className="prose prose-sm max-w-none text-[15px] leading-relaxed whitespace-pre-wrap text-slate-700">{selected.content}</div>
        </main>
        <footer className="border-t px-4 py-4 mt-12 print:hidden">
          <div className="max-w-2xl mx-auto flex items-center justify-center gap-1.5">
            <img src="/assets/logo.png" alt="" className="h-3.5 w-3.5 opacity-40" />
            <p className="text-[10px] text-slate-400">Powered by LodgiFlow</p>
          </div>
        </footer>
      </div>
    );
  }

  // Article list view
  return (
    <div className="min-h-screen bg-slate-50" data-testid="kb-portal">
      <header className="bg-white border-b px-4 py-5">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="h-5 w-5 text-blue-600" />
            <h1 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Help Center</h1>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="Search articles..." value={search} onChange={e => setSearch(e.target.value)}
              className="pl-10 h-10 bg-slate-50 border-slate-200 text-sm" data-testid="kb-search" />
          </div>
          <div className="flex gap-2 mt-3 overflow-x-auto">
            <button onClick={() => { setFilterCat(''); setFilterPlatform(''); }}
              className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${!filterCat && !filterPlatform ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>All</button>
            {Object.entries(CATS).map(([id, cat]) => (
              <button key={id} onClick={() => { setFilterCat(id); setFilterPlatform(''); }}
                className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${filterCat === id ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>{cat.label}</button>
            ))}
          </div>
          {platforms.length > 1 && (
            <div className="flex gap-2 mt-2 overflow-x-auto">
              {platforms.map(p => (
                <button key={p} onClick={() => { setFilterPlatform(p); setFilterCat(''); }}
                  className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${filterPlatform === p ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>{p}</button>
              ))}
            </div>
          )}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        {filtered.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen className="h-10 w-10 mx-auto mb-3 text-slate-300" />
            <p className="text-sm text-slate-500">No articles found</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(a => {
              const cat = CATS[a.category];
              return (
                <button key={a.id} onClick={() => setSelected(a)} data-testid={`kb-article-${a.id}`}
                  className="w-full text-left bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md hover:border-slate-300 transition-all">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-slate-900 mb-1">{a.title}</h3>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px] text-slate-500">{a.platform_name}</Badge>
                        {cat && <Badge className={`text-[9px] ${cat.color}`}>{cat.label}</Badge>}
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-slate-300 shrink-0 mt-0.5" />
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </main>

      <footer className="border-t bg-white px-4 py-4 mt-8">
        <div className="max-w-2xl mx-auto flex items-center justify-center gap-1.5">
          <img src="/assets/logo.png" alt="" className="h-3.5 w-3.5 opacity-40" />
          <p className="text-[10px] text-slate-400">Powered by LodgiFlow</p>
        </div>
      </footer>
    </div>
  );
}
